import React from 'react';
import noop from 'lodash/noop';
import { SOFTWARE_PROJECT } from '@atlassian/jira-common-constants/src/project-types.tsx';
import { AddPeopleModalBoundary } from '@atlassian/jira-project-add-people/src/async.tsx';
import type AddPeopleModalType from '@atlassian/jira-project-add-people/src/index.tsx';
import type { Party, PartyId, Role } from '@atlassian/jira-project-add-people/src/model/index.tsx';
import { lazy, LazyWait } from '@atlassian/react-loosely-lazy';
import { BACKLOG_PACKAGE_NAME } from '../../../../../common/constants/analytics.tsx';
import { getProjectKey, projectIdSelector } from '../../../../../state/selectors/app/index.tsx';
import { useBacklogSelector } from '../../../../../state/index.tsx';

type OwnProps = {
	isOpen: boolean;
	accessLevel?: string;
	adminRoleId?: number;
	defaultRoleId?: number;
	partiesInProject?: Record<PartyId, Party>;
	projectRoles?: Role[];
	roleConfigurable?: boolean;
	onStopPerfMetric: (() => void) | undefined;
	onClose: (() => void) | undefined;
	onAddSuccess: (() => void) | undefined;
	onError: (() => void) | undefined;
};

// eslint-disable-next-line jira/deprecations/no-rll-client-async-experiences
const AddPeopleModal = lazy<typeof AddPeopleModalType>(
	() =>
		import(
			/* webpackChunkName: "async-project-add-people-dialog" */ '@atlassian/jira-project-add-people'
		),
);

const AddPeopleModalWrapper = ({
	isOpen,
	onClose = noop,
	onError = noop,
	onAddSuccess = noop,
	onStopPerfMetric = noop,
	...props
}: OwnProps) => {
	const projectId = useBacklogSelector(projectIdSelector);
	const projectKey = useBacklogSelector(getProjectKey);
	return (
		<AddPeopleModalBoundary packageName={BACKLOG_PACKAGE_NAME}>
			<LazyWait until={isOpen}>
				<AddPeopleModal
					projectId={projectId}
					projectKey={projectKey}
					isOpen={isOpen}
					onClose={onClose}
					onError={onError}
					onAddSuccess={onAddSuccess}
					onStopPerfMetric={onStopPerfMetric}
					projectType={SOFTWARE_PROJECT}
					isAdmin
					analyticsPrefix="software.backlog"
					allowFlags
					{...props}
				/>
			</LazyWait>
		</AddPeopleModalBoundary>
	);
};

export default AddPeopleModalWrapper;
